import React, { useState } from 'react';

interface CardManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReplaceCard: () => void;
  onDeleteCard: () => void;
  cardInfo: {
    id: string;
    maskedCardNumber: string;
  };
}

const CardManagementModal: React.FC<CardManagementModalProps> = ({
  isOpen,
  onClose,
  onReplaceCard,
  onDeleteCard,
  cardInfo
}) => {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const formatCardNumber = (maskedNumber: string): string => {
    if (!maskedNumber) return '';
    
    // Убираем все звездочки и получаем только цифры
    const digits = maskedNumber.replace(/\*/g, '');
    
    if (digits.length < 8) return maskedNumber; // Если цифр меньше 8, возвращаем как есть
    
    // Берем первые 4 и последние 4 цифры
    const firstFour = digits.slice(0, 4);
    const lastFour = digits.slice(-4);
    
    return `${firstFour} **** **** ${lastFour}`;
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = () => {
    onDeleteCard();
    setShowDeleteConfirmation(false);
    onClose();
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Main Bottom Sheet */}
      <div className="fixed inset-0 z-50 flex items-end justify-center">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />
        
        {/* Bottom Sheet */}
        <div className="relative bg-[#141826] rounded-t-3xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="text-white text-left p-4 relative border-b border-white/10">
            <button
              onClick={onClose}
              className="absolute right-4 top-4 w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-white hover:bg-slate-600 transition-colors"
            >
              ✕
            </button>
            <h3 className="text-2xl text-left">Управление картой</h3>
          </div>
          
          {/* Body */}
          <div className="text-white p-4">
            {/* Card Display */}
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-4 mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-[#2e3036] rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <div>
                                     <div className="text-white text-lg">{formatCardNumber(cardInfo.maskedCardNumber)}</div>
                  <div className="text-white/70 text-sm">Банковская карта</div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {/* Delete Card Button */}
              <button
                onClick={handleDeleteClick}
                className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-white/10 text-white hover:bg-white/20 active:scale-95"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                <span>Удалить карту</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={handleCancelDelete}
          />
          
          {/* Modal */}
          <div className="relative bg-[#141826] rounded-2xl w-full max-w-sm mx-4 p-6">
            {/* Header */}
            <div className="text-white text-center mb-6">
              <h3 className="text-2xl mb-2">Удалить карту?</h3>
              <p className="text-slate-400 text-sm">
                Вы уверены, что хотите удалить банковскую карту?
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={handleCancelDelete}
                className="flex-1 py-3 px-4 rounded-full bg-slate-700 text-white hover:bg-slate-600 transition-colors"
              >
                Отменить
              </button>
              <button
                onClick={handleConfirmDelete}
                className="flex-1 py-3 px-4 rounded-full bg-white text-gray-800 hover:bg-white/90 transition-colors"
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CardManagementModal;
